import { createSlice } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "react-redux-loading-bar";

import * as Api from "./roleEditorAPI";
import { getStaffRoles } from "./staffAPI";
import { staffErrorMessage } from "./staffSlice";

/**
 * The role editor sits behind a **different flag** from the rest of staff administration, so a 404
 * here means something else and has to say so.
 *
 * `GET /staff-roles` is the staff administration role picker and answers to `staff-admin`; creating, recomposing
 * and deleting a role answer to `role-editor`, which exists to be switched off on its own. Reusing
 * the staff message would send somebody to turn on a flag that is already on.
 */
export const roleEditorErrorMessage = (error) => {
  const status = error.response?.status;
  if (status === 404 && !error.response?.data) {
    return "The role editor is switched off in this environment. Roles stay readable and grantable, but nothing here can be changed until parkit.rbac.role-editor.enabled is turned on in parkit-user-store.";
  }
  if (status === 403) {
    return "Your account does not hold Super Admin, so it cannot change role composition.";
  }
  return staffErrorMessage(error);
};

const initialState = {
  roles: [],
  rolesStatus: "idle",
  catalogue: [],
  catalogueStatus: "idle",

  /** The role open in the composition editor, and the composition being proposed for it. */
  editing: null,
  draft: [],

  preview: null,
  previewStatus: "idle",

  /**
   * Null until the operator chooses, so the editor can default it on for a sensitive removal and
   * still tell "they said no" from "they have not been asked yet".
   */
  terminateSessions: null,

  createModalOpen: false,
  catalogueModalOpen: false,
  confirmDelete: null,

  pendingAction: "",
  error: "",
  successMessage: "",
  warningMessage: "",
};

export const roleEditorSlice = createSlice({
  name: "roleEditor",
  initialState,
  reducers: {
    setRoles: (state, action) => {
      state.roles = action.payload;
      state.rolesStatus = "succeeded";
    },
    setRolesStatus: (state, action) => {
      state.rolesStatus = action.payload;
    },
    setCatalogue: (state, action) => {
      state.catalogue = action.payload;
      state.catalogueStatus = "succeeded";
    },
    setCatalogueStatus: (state, action) => {
      state.catalogueStatus = action.payload;
    },
    setEditing: (state, action) => {
      state.editing = action.payload;
      state.draft = action.payload ? action.payload.permissions.map(({ key }) => key) : [];
      state.preview = null;
      state.previewStatus = "idle";
      state.terminateSessions = null;
      state.error = "";
    },
    toggleDraftKey: (state, action) => {
      const key = action.payload;
      state.draft = state.draft.includes(key)
        ? state.draft.filter((existing) => existing !== key)
        : [...state.draft, key];
      // Any edit invalidates a preview taken against the previous composition. Keeping a stale one
      // on screen would show a blast radius for a change nobody is about to make.
      state.preview = null;
      state.previewStatus = "idle";
      state.terminateSessions = null;
    },
    setPreview: (state, action) => {
      state.preview = action.payload;
      state.previewStatus = "succeeded";
      state.terminateSessions = action.payload.removesSensitive ? true : null;
    },
    setPreviewStatus: (state, action) => {
      state.previewStatus = action.payload;
    },
    /** Returns the editor to the checkboxes with the draft intact, so a "Back" loses no work. */
    clearPreview: (state) => {
      state.preview = null;
      state.previewStatus = "idle";
      state.terminateSessions = null;
    },
    setTerminateSessions: (state, action) => {
      state.terminateSessions = action.payload;
    },
    setCreateModalOpen: (state, action) => {
      state.createModalOpen = action.payload;
      state.error = "";
    },
    setCatalogueModalOpen: (state, action) => {
      state.catalogueModalOpen = action.payload;
    },
    setConfirmDelete: (state, action) => {
      state.confirmDelete = action.payload;
      state.error = "";
    },
    setPendingAction: (state, action) => {
      state.pendingAction = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setWarningMessage: (state, action) => {
      state.warningMessage = action.payload;
    },
  },
});

export const {
  setRoles,
  setRolesStatus,
  setCatalogue,
  setCatalogueStatus,
  setEditing,
  toggleDraftKey,
  setPreview,
  setPreviewStatus,
  clearPreview,
  setTerminateSessions,
  setCreateModalOpen,
  setCatalogueModalOpen,
  setConfirmDelete,
  setPendingAction,
  setError,
  setSuccessMessage,
  setWarningMessage,
} = roleEditorSlice.actions;

export const selectRoleEditor = (state) => state.roleEditor;

/** Reads the staff-admin role list, so this is the one call that answers to the staff-admin flag. */
export const handleGetRoles = () => async (dispatch) => {
  dispatch(setRolesStatus("loading"));
  dispatch(setError(""));
  try {
    const response = await getStaffRoles();
    dispatch(setRoles(response.data));
  } catch (error) {
    dispatch(setRolesStatus("failed"));
    dispatch(setError(staffErrorMessage(error)));
  }
};

export const handleGetCatalogue = () => async (dispatch) => {
  dispatch(setCatalogueStatus("loading"));
  try {
    const response = await Api.getPermissionCatalogue();
    dispatch(setCatalogue(response.data));
  } catch (error) {
    dispatch(setCatalogueStatus("failed"));
    dispatch(setError(roleEditorErrorMessage(error)));
  }
};

export const handleCreateRole = (request) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction("create"));
  dispatch(setError(""));
  try {
    const response = await Api.createRole(request);
    dispatch(setCreateModalOpen(false));
    dispatch(setSuccessMessage(
      `${response.data.code} created with ${response.data.permissions.length} permission${
        response.data.permissions.length === 1 ? "" : "s"
      }. Nobody holds it until it is granted on a staff account.`
    ));
    await dispatch(handleGetRoles());
    await dispatch(handleGetCatalogue());
    return true;
  } catch (error) {
    dispatch(setError(roleEditorErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handlePreview = (code, permissions) => async (dispatch) => {
  dispatch(setPreviewStatus("loading"));
  dispatch(setError(""));
  try {
    const response = await Api.previewRolePermissions(code, permissions);
    dispatch(setPreview(response.data));
    return true;
  } catch (error) {
    dispatch(setPreviewStatus("failed"));
    dispatch(setError(roleEditorErrorMessage(error)));
    return false;
  }
};

export const handleSetPermissions = (code, permissions, terminateSessions) => async (dispatch, getState) => {
  const { preview } = getState().roleEditor;
  dispatch(showLoading());
  dispatch(setPendingAction(`save:${code}`));
  dispatch(setError(""));
  try {
    const response = await Api.setRolePermissions(code, permissions, terminateSessions);
    const { role, effectiveWithinMinutes, sessionsTerminatedFor } = response.data;
    const ended = sessionsTerminatedFor ?? [];

    dispatch(setEditing(null));
    dispatch(setSuccessMessage(
      ended.length > 0
        ? `${role.code} updated and ${ended.length} ${ended.length === 1 ? "person was" : "people were"} signed out. They get the new permission set as soon as they sign back in.`
        : `${role.code} updated. Holders see the change within ${effectiveWithinMinutes} minutes, or straight away if they sign out and back in.`
    ));

    // Asking for termination and getting none, on a change that removed a sensitive permission from
    // somebody, means the sign-out silently did not happen and the removal is live for the whole
    // token window. That is the one outcome here worth interrupting for.
    if (terminateSessions && ended.length === 0 && preview?.removesSensitive && preview?.holders?.length > 0) {
      dispatch(setWarningMessage(
        `${role.code} was updated, but nobody was signed out even though a sensitive permission was removed and ${preview.holders.length} ${preview.holders.length === 1 ? "person holds" : "people hold"} the role. Treat the removed access as still granted for up to ${effectiveWithinMinutes} minutes, and check Keycloak sessions before relying on this change.`
      ));
    }

    await dispatch(handleGetRoles());
    await dispatch(handleGetCatalogue());
    return true;
  } catch (error) {
    dispatch(setError(roleEditorErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handleDeleteRole = (code) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction(`delete:${code}`));
  dispatch(setError(""));
  try {
    await Api.deleteRole(code);
    dispatch(setConfirmDelete(null));
    dispatch(setSuccessMessage(`${code} deleted. The audit trail keeps what it used to grant.`));
    await dispatch(handleGetRoles());
    await dispatch(handleGetCatalogue());
    return true;
  } catch (error) {
    dispatch(setError(roleEditorErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export default roleEditorSlice.reducer;
